export interface IApplicationTableColumnSettings {
    order: string[]
    visible: string[]
    widths: Record<string, number>
}

const FIXED_COLUMN_NAME = 'application_id'
const MIN_STORED_COLUMN_WIDTH = 60
const MAX_STORED_COLUMN_WIDTH = 1200
const getStorageKey = (loginName: string) => `crm:applications-table-columns:${encodeURIComponent(loginName)}`

const unique = (values: string[]) => [...new Set(values)]

type StoredApplicationTableColumnSettings = Omit<IApplicationTableColumnSettings, 'widths'> & { widths?: unknown }

const isStoredSettings = (value: unknown): value is StoredApplicationTableColumnSettings => {
    if (!value || typeof value !== 'object') return false

    const settings = value as Partial<IApplicationTableColumnSettings>
    return Array.isArray(settings.order) && settings.order.every(name => typeof name === 'string')
        && Array.isArray(settings.visible) && settings.visible.every(name => typeof name === 'string')
}

export const normalizeApplicationTableColumnSettings = (
    availableColumnNames: string[],
    storedSettings?: StoredApplicationTableColumnSettings,
): IApplicationTableColumnSettings => {
    const availableNames = unique(availableColumnNames)
    const availableNameSet = new Set(availableNames)
    const storedOrder = storedSettings?.order || []
    const knownStoredOrder = unique(storedOrder).filter(name => availableNameSet.has(name) && name !== FIXED_COLUMN_NAME)
    const newColumnNames = availableNames.filter(name => name !== FIXED_COLUMN_NAME && !knownStoredOrder.includes(name))
    const order = availableNameSet.has(FIXED_COLUMN_NAME)
        ? [FIXED_COLUMN_NAME, ...knownStoredOrder, ...newColumnNames]
        : [...knownStoredOrder, ...newColumnNames]

    if (!storedSettings) return { order, visible: [...order], widths: {} }

    const visibleNameSet = new Set(storedSettings.visible.filter(name => availableNameSet.has(name)))
    newColumnNames.forEach(name => visibleNameSet.add(name))
    if (availableNameSet.has(FIXED_COLUMN_NAME)) visibleNameSet.add(FIXED_COLUMN_NAME)

    const storedWidths = storedSettings.widths && typeof storedSettings.widths === 'object' && !Array.isArray(storedSettings.widths)
        ? storedSettings.widths as Record<string, unknown>
        : {}
    const widths = Object.fromEntries(Object.entries(storedWidths).flatMap(([name, width]) => {
        if (name === FIXED_COLUMN_NAME || !availableNameSet.has(name) || typeof width !== 'number' || !Number.isFinite(width)) return []
        return [[name, Math.min(MAX_STORED_COLUMN_WIDTH, Math.max(MIN_STORED_COLUMN_WIDTH, Math.round(width)))]]
    }))

    return { order, visible: order.filter(name => visibleNameSet.has(name)), widths }
}

export const loadApplicationTableColumnSettings = (
    loginName: string,
    availableColumnNames: string[],
): IApplicationTableColumnSettings => {
    if (!loginName) return normalizeApplicationTableColumnSettings(availableColumnNames)

    try {
        const storedValue: unknown = JSON.parse(localStorage.getItem(getStorageKey(loginName)) || 'null')
        return normalizeApplicationTableColumnSettings(availableColumnNames, isStoredSettings(storedValue) ? storedValue : undefined)
    } catch {
        return normalizeApplicationTableColumnSettings(availableColumnNames)
    }
}

export const moveApplicationTableColumn = (
    settings: IApplicationTableColumnSettings,
    activeName: string,
    overName: string,
): IApplicationTableColumnSettings => {
    if (activeName === FIXED_COLUMN_NAME || activeName === overName) return settings

    const oldIndex = settings.order.indexOf(activeName)
    const newIndex = settings.order.indexOf(overName)
    if (oldIndex < 0 || newIndex < 0) return settings

    const order = [...settings.order]
    const [movedName] = order.splice(oldIndex, 1)
    order.splice(newIndex, 0, movedName)

    return {
        ...settings,
        order: order.includes(FIXED_COLUMN_NAME)
            ? [FIXED_COLUMN_NAME, ...order.filter(name => name !== FIXED_COLUMN_NAME)]
            : order,
    }
}

export const saveApplicationTableColumnSettings = (
    loginName: string,
    settings: IApplicationTableColumnSettings,
): boolean => {
    if (!loginName) return false

    try {
        localStorage.setItem(getStorageKey(loginName), JSON.stringify(settings))
        return true
    } catch {
        return false
    }
}